import { useState, useEffect } from "react";

import {
  Plus,
  Image,
  Palette,
  Paperclip,
  Images,
  AtSign,
  LayoutPanelLeft,
  Search,
  Check,
  X,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

import { cn } from "@/lib/utils";
import { useFeatures } from "@/hooks/useFeatures";
import { skillsMarketplaceApi } from "@/lib/api/skills-marketplace";
import type { InstalledSkill, CustomSkillItem } from "@optidev/shared-types";

interface AttachmentDropdownProps {
  onSelectAttach?: () => void;
  onSelectConnections?: () => void;
  onSelectProjectSettings?: () => void;
  onSelectImageGenerator?: () => void;
  onSelectMediaLibrary?: () => void;
  onSelectThemes?: () => void;
  onSelectAddReference?: () => void;
  onSelectSkill?: (skill: {
    id: string;
    name: string;
    description: string;
  }) => void;
  selectedSkillIds?: string[];
  disabled?: boolean;
  isMobile: boolean;
}

export function AttachmentDropdown({
  onSelectAttach,
  onSelectImageGenerator,
  onSelectMediaLibrary,
  onSelectThemes,
  onSelectAddReference,
  onSelectSkill,
  selectedSkillIds = [],
  disabled = false,
  isMobile,
}: AttachmentDropdownProps) {
  const { features } = useFeatures();
  const [open, setOpen] = useState(false);
  const [marketplaceSkills, setMarketplaceSkills] = useState<InstalledSkill[]>([]);
  const [customSkills, setCustomSkills] = useState<CustomSkillItem[]>([]);
  const [isLoadingSkills, setIsLoadingSkills] = useState(false);
  const [skillSearch, setSkillSearch] = useState("");
  const [subOpen, setSubOpen] = useState(false);
  // Mobile: bottom sheet
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);

  useEffect(() => {
    const shouldFetch = isMobile ? mobileSheetOpen : subOpen;
    if (!shouldFetch) return;
    setSkillSearch("");
    setIsLoadingSkills(true);
    Promise.all([
      skillsMarketplaceApi.getInstalled(),
      skillsMarketplaceApi.getCustomSkills(),
    ])
      .then(([installed, custom]) => {
        setMarketplaceSkills(installed);
        setCustomSkills(custom);
      })
      .catch(() => {})
      .finally(() => setIsLoadingSkills(false));
  }, [subOpen, mobileSheetOpen, isMobile]);

  const q = skillSearch.toLowerCase();
  const filteredCustom = customSkills.filter(
    (s) =>
      s.name.toLowerCase().includes(q) ||
      (s.description ?? "").toLowerCase().includes(q),
  );
  const filteredMarketplace = marketplaceSkills.filter(
    (s) =>
      s.name.toLowerCase().includes(q) ||
      s.description.toLowerCase().includes(q),
  );
  const hasAny = filteredCustom.length > 0 || filteredMarketplace.length > 0;
  const hasNoneTotal = customSkills.length === 0 && marketplaceSkills.length === 0;

  const skillListContent = (
    <>
      {/* Search */}
      <div className="p-2 border-b border-border">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search skills..."
            value={skillSearch}
            onChange={(e) => setSkillSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className="w-full pl-8 pr-3 py-1.5 text-sm bg-transparent border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      {/* List */}
      <div className="overflow-y-auto flex-1 max-h-[40vh]">
        {isLoadingSkills ? (
          <div className="space-y-1 p-1">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-2.5 px-3 py-2.5 animate-pulse">
                <div className="h-4 w-4 rounded bg-muted flex-shrink-0 mt-0.5" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 w-24 rounded bg-muted" />
                  <div className="h-3 w-full rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : !hasAny ? (
          <div className="px-3 py-6 text-center">
            {hasNoneTotal ? (
              <>
                <p className="text-sm text-muted-foreground mb-2">No skills yet.</p>
                <a
                  href="/dashboard/skills"
                  className="text-sm text-primary hover:underline font-medium"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.href = "/dashboard/skills";
                  }}
                >
                  Browse Skills Marketplace →
                </a>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No matching skills</p>
            )}
          </div>
        ) : (
          <>
            {filteredCustom.length > 0 && (
              <>
                <div className="px-3 pt-2.5 pb-1">
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                    Custom Skills
                  </span>
                </div>
                {filteredCustom.map((skill) => {
                  const slug = skill.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                  const resolvedId = `custom-${slug}`;
                  const isSelected = selectedSkillIds.includes(resolvedId);
                  return (
                    <button
                      key={resolvedId}
                      type="button"
                      className={cn(
                        "w-full flex items-start gap-2.5 px-3 py-3 text-left transition-colors",
                        isSelected ? "bg-emerald-500/10 hover:bg-emerald-500/15" : "hover:bg-accent/50",
                      )}
                      onClick={() => {
                        onSelectSkill?.({ id: resolvedId, name: skill.name, description: skill.description ?? "" });
                        setSubOpen(false);
                        setMobileSheetOpen(false);
                        setOpen(false);
                      }}
                    >
                      <LayoutPanelLeft className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-foreground truncate">{skill.name}</div>
                        <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                          {skill.description}
                        </div>
                      </div>
                      {isSelected && <Check className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />}
                    </button>
                  );
                })}
              </>
            )}
            {filteredMarketplace.length > 0 && (
              <>
                <div className="px-3 pt-2.5 pb-1">
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                    Installed Skills
                  </span>
                </div>
                {filteredMarketplace.map((skill) => {
                  const isSelected = selectedSkillIds.includes(skill.id);
                  return (
                    <button
                      key={skill.id}
                      type="button"
                      className={cn(
                        "w-full flex items-start gap-2.5 px-3 py-3 text-left transition-colors",
                        isSelected ? "bg-purple-500/10 hover:bg-purple-500/15" : "hover:bg-accent/50",
                      )}
                      onClick={() => {
                        onSelectSkill?.({ id: skill.id, name: skill.name, description: skill.description });
                        setSubOpen(false);
                        setMobileSheetOpen(false);
                        setOpen(false);
                      }}
                    >
                      <LayoutPanelLeft className="h-4 w-4 text-purple-500 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-foreground truncate">{skill.name}</div>
                        <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                          {skill.description}
                        </div>
                      </div>
                      {isSelected && <Check className="h-3.5 w-3.5 text-purple-500 flex-shrink-0 mt-0.5" />}
                    </button>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>
    </>
  );

  return (
    <>
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          className={cn(
            "p-0",
            isMobile ? "h-9 w-9 rounded-xl bg-muted" : "h-8 w-8",
          )}
        >
          <Plus className={cn(isMobile ? "w-5 h-5" : "w-4 h-4")} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={cn(isMobile ? "w-64" : "w-48")}>
        <DropdownMenuItem onClick={onSelectAttach} disabled={disabled}>
          <Paperclip className="w-4 h-4 mr-2" />
          <span>Attach files</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onSelectAddReference} disabled={disabled}>
          <AtSign className="w-4 h-4 mr-2" />
          <span>Add reference</span>
        </DropdownMenuItem>

        {isMobile ? (
          /* Mobile: bottom sheet trigger */
          <DropdownMenuItem
            disabled={disabled}
            onClick={() => {
              setOpen(false);
              setMobileSheetOpen(true);
            }}
          >
            <LayoutPanelLeft className="w-4 h-4 mr-2" />
            <span>Use a skill</span>
          </DropdownMenuItem>
        ) : (
          /* Desktop: side submenu */
          <DropdownMenuSub open={subOpen} onOpenChange={setSubOpen}>
            <DropdownMenuSubTrigger disabled={disabled} className="flex items-center">
              <LayoutPanelLeft className="w-4 h-4 mr-2" />
              <span>Use a skill</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-80 p-0" sideOffset={4} alignOffset={-200}>
              {skillListContent}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}

        <DropdownMenuSeparator />
        {!isMobile && (
          <DropdownMenuItem onClick={onSelectThemes} disabled={disabled}>
            <Palette className="w-4 h-4 mr-2" />
            <span>Themes</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={onSelectImageGenerator} disabled={disabled}>
          <Image className="w-4 h-4 mr-2" />
          <span>Image Generator</span>
        </DropdownMenuItem>
        {features.mediaLibrary && (
          <DropdownMenuItem onClick={onSelectMediaLibrary} disabled={disabled}>
            <Images className="w-4 h-4 mr-2" />
            <span>Media Library</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>

    {/* Mobile: bottom sheet for skill selection */}
    <Sheet open={mobileSheetOpen} onOpenChange={setMobileSheetOpen}>
      <SheetContent side="bottom" hideCloseButton className="p-0 rounded-t-2xl max-h-[75vh] flex flex-col">
        <SheetHeader className="px-4 pt-4 pb-2 flex-row items-center justify-between shrink-0">
          <SheetTitle className="flex items-center gap-2 text-base">
            <LayoutPanelLeft className="h-4 w-4 text-purple-500" />
            Use a skill
          </SheetTitle>
          <button
            onClick={() => setMobileSheetOpen(false)}
            className="rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </SheetHeader>
        <div className="border-t border-border flex flex-col flex-1 min-h-0 overflow-hidden">
          {skillListContent}
        </div>
      </SheetContent>
    </Sheet>
    </>
  );
}
